import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaChevronDown, FaDownload } from 'react-icons/fa';
import Header from './Header';
import api from './services/apiService';
import { authService } from './services/authService';
import { getApiBaseUrl } from './utils/apiBaseUrl';
import './scss/playerSignOnListPage.scss';

const FIELD_LABELS = {
  submitted_at: 'Submitted',
  team_id: 'Team ID',
  team_name: 'Team',
  parent_id: 'Parent ID',
  player_id: 'Player ID',
  reused_parent: 'Existing parent account',
  reused_player: 'Existing player account',
  player_team_status: 'Player team status',
  other_teams_leagues: 'Other teams / leagues',
  first_name: 'First name',
  first_names: 'First name(s)',
  last_name: 'Last name',
  date_of_birth: 'Date of birth',
  email: 'Email',
  phone: 'Phone',
  fan: 'FAN',
  relationship: 'Relationship to child',
  line1: 'Address line 1',
  line2: 'Address line 2',
  town: 'Town',
  postcode: 'Postcode',
  emergency_contacts: 'Emergency contacts',
  primary: 'Primary emergency contact',
  secondary: 'Second emergency contact',
  name: 'Name',
  team_age_group: 'Team age group',
  school_sept_2026: 'School (Sept 2026)',
  looked_after_child: 'Looked after child',
  allergies: 'Allergies',
  prescribed_medicines: 'Prescribed medicines',
  medical_conditions: 'Medical conditions',
  learning_ehcp: 'Learning / EHCP',
  answer: 'Answer',
  details: 'Details',
  perm_training_matches: 'Permission: training & matches',
  perm_photos_social: 'Permission: photos / social',
  perm_match_reports: 'Permission: match reports',
  medical_treatment: 'Medical treatment',
  code_of_conduct: 'Code of conduct',
  respect: 'Respect',
  social_media_policy: 'Social media policy',
  termination: 'Termination',
  data_protection: 'Data protection',
};

function formatLabel(key) {
  if (FIELD_LABELS[key]) {
    return FIELD_LABELS[key];
  }
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

function DetailSection({ title, data, excludeKeys = [] }) {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const entries = Object.entries(data).filter(([key]) => !excludeKeys.includes(key));
  if (entries.length === 0) {
    return null;
  }

  return (
    <section className="signon-detail-section">
      {title && <h3>{title}</h3>}
      <dl>
        {entries.map(([key, value]) => {
          if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            if ('answer' in value || 'details' in value) {
              return (
                <div key={key} className="signon-detail-row nested">
                  <dt>{formatLabel(key)}</dt>
                  <dd>
                    <span className="answer">{formatValue(value.answer)}</span>
                    {value.details ? (
                      <span className="details">{formatValue(value.details)}</span>
                    ) : null}
                  </dd>
                </div>
              );
            }

            return (
              <div key={key} className="signon-detail-group">
                <h4>{formatLabel(key)}</h4>
                <DetailSection data={value} />
              </div>
            );
          }

          return (
            <div key={key} className="signon-detail-row">
              <dt>{formatLabel(key)}</dt>
              <dd>{formatValue(value)}</dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

function getSignonChildName(item) {
  return (
    item.child_display_name ||
    `${item.child?.first_name || ''} ${item.child?.last_name || ''}`.trim() ||
    ''
  );
}

function getSignonsSinceBaseline() {
  return sessionStorage.getItem('previous_last_login') || '';
}

function PlayerSignOnListPage() {
  const navigate = useNavigate();
  const [signons, setSignons] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedFile, setExpandedFile] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);
  const [listDebug, setListDebug] = useState(null);
  const [assignedTeamIds, setAssignedTeamIds] = useState([]);
  const [assignedTeams, setAssignedTeams] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const initAccess = async () => {
      if (!authService.isClubSecretary() && !authService.isSuperAdmin()) {
        if (authService.isManager()) {
          try {
            const infoResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_my_manager_info.php`);
            if (infoResponse.data?.success && infoResponse.data.manager_type === 'Club Secretary') {
              sessionStorage.setItem('manager_type', 'Club Secretary');
              setAssignedTeams(infoResponse.data.teams || []);
            } else {
              navigate('/team-selection', { replace: true });
              return;
            }
          } catch (err) {
            console.error(err);
            navigate('/team-selection', { replace: true });
            return;
          }
        } else {
          navigate('/team-selection', { replace: true });
          return;
        }
      } else if (authService.isClubSecretary() && !authService.isSuperAdmin()) {
        try {
          const infoResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_my_manager_info.php`);
          if (infoResponse.data?.success) {
            sessionStorage.setItem('manager_type', 'Club Secretary');
            setAssignedTeams(infoResponse.data.teams || []);
          }
        } catch (err) {
          console.error(err);
        }
      }

      setLoading(true);
      setError('');
      try {
        const since = getSignonsSinceBaseline();
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/player_signon/list_signons.php`,
          {
            params: since ? { since } : {},
          }
        );
        if (response.data?.success) {
          setSignons(response.data.signons || []);
          setListDebug(response.data.debug || null);
          setAssignedTeamIds(response.data.assigned_team_ids || []);
          setNewCount(response.data.new_count || 0);
          if (response.data.debug) {
            console.log('[Sign-ons] Debug:', response.data.debug);
          }
        } else {
          setError(response.data?.message || 'Unable to load player sign-ons.');
        }
      } catch (err) {
        console.error('Error loading player sign-ons:', err);
        setError(err.response?.data?.message || 'Unable to load player sign-ons.');
      } finally {
        setLoading(false);
      }
    };

    initAccess();
  }, [navigate]);

  const handleRowClick = async (file) => {
    if (expandedFile === file) {
      setExpandedFile(null);
      return;
    }

    setExpandedFile(file);

    if (detailCache[file]) {
      return;
    }

    setDetailLoading(file);
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/player_signon/list_signons.php`,
        { params: { file } }
      );
      if (response.data?.success && response.data.signon) {
        setDetailCache((prev) => ({
          ...prev,
          [file]: response.data.signon,
        }));
      } else {
        setError(response.data?.message || 'Unable to load sign-on details.');
      }
    } catch (err) {
      console.error('Error loading sign-on detail:', err);
      setError(err.response?.data?.message || 'Unable to load sign-on details.');
    } finally {
      setDetailLoading(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const since = getSignonsSinceBaseline();
      const params = new URLSearchParams({ export: 'csv' });
      if (since) {
        params.set('since', since);
      }

      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/player_signon/list_signons.php?${params.toString()}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `player-signons-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting sign-ons:', err);
      setError('Unable to export sign-ons. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatSubmittedAt = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const normalisedQuery = searchQuery.trim().toLowerCase();
  const filteredSignons = !normalisedQuery
    ? signons
    : signons.filter((item) => {
        const teamName = (item.team_name || '').toLowerCase();
        const childName = getSignonChildName(item).toLowerCase();
        return teamName.includes(normalisedQuery) || childName.includes(normalisedQuery);
      });

  return (
    <div className="PlayerSignOnListPage container">
      <Header />
      <button
        type="button"
        className="back-link"
        onClick={() => navigate('/team-selection')}
      >
        <FaChevronLeft size={12} /> Back to team selection
      </button>

      <h1>Player Sign-ons</h1>
      <p className="page-intro">
        Review registration forms submitted via the player sign-on page for your assigned teams.
        {newCount > 0 && (
          <span className="new-count-badge">{newCount} new since your last login</span>
        )}
      </p>

      {!loading && !error && (
        <div className="signon-toolbar">
          <button
            type="button"
            className="export-btn"
            onClick={handleExport}
            disabled={exporting || signons.length === 0}
            title={signons.length === 0 ? 'No sign-ons to export' : 'Download sign-ons as CSV'}
          >
            <FaDownload size={14} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      )}

      {!loading && signons.length > 0 && (
        <div className="signon-search">
          <label htmlFor="signon-search-input" className="visually-hidden">
            Search by team or player name
          </label>
          <input
            id="signon-search-input"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by team or player name"
            autoComplete="off"
          />
          {normalisedQuery && (
            <span className="search-count">
              {filteredSignons.length} of {signons.length}
            </span>
          )}
        </div>
      )}

      {loading && <div className="status-message">Loading sign-ons…</div>}
      {error && <div className="status-message error">{error}</div>}

      {!loading && !error && signons.length === 0 && (
        <div className="status-message">
          <p>No player sign-ons have been submitted yet for your assigned teams.</p>
          {!authService.isSuperAdmin() && assignedTeams.length > 0 && (
            <p className="empty-hint">
              Your assigned teams:{' '}
              {assignedTeams.map((team) => team.team_name).join(', ')}
            </p>
          )}
          {!authService.isSuperAdmin() && assignedTeams.length === 0 && (
            <p className="empty-hint">
              No teams are currently assigned to your club secretary account. Ask a super admin to assign teams via Admin → Assign Club Secretary.
            </p>
          )}
          {(process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENABLE_DEBUGGER === 'true') && listDebug && (
            <div className="signon-debug-panel">
              <p><strong>Debug</strong> (API: {process.env.REACT_APP_API_URL})</p>
              <p>Manager ID: {listDebug.manager_id ?? '—'}</p>
              <p>Assigned team IDs: {listDebug.is_super_admin ? 'all (super admin)' : JSON.stringify(assignedTeamIds)}</p>
              <p>Sign-on files on server: {listDebug.total_files_scanned ?? 0}</p>
              <p>Team IDs in sign-on files: {JSON.stringify(listDebug.team_ids_in_files || [])}</p>
              <p>Local API base (sign-on submit): {getApiBaseUrl() || '—'}</p>
              <p className="empty-hint">
                If sign-ons were submitted locally but this page uses the remote API, they will not appear here. Sign out and back in only if you were just assigned the club secretary role.
              </p>
            </div>
          )}
        </div>
      )}

      {!loading && !error && signons.length > 0 && filteredSignons.length === 0 && (
        <div className="status-message">No sign-ons match your search.</div>
      )}

      <div className="signon-list">
        {filteredSignons.map((item) => {
          const isExpanded = expandedFile === item.file;
          const detail = detailCache[item.file];
          const childName = getSignonChildName(item) || 'Unknown child';

          return (
            <div
              key={item.file}
              className={`signon-card ${isExpanded ? 'expanded' : ''} ${item.is_new ? 'is-new' : ''}`}
            >
              <button
                type="button"
                className="signon-summary"
                onClick={() => handleRowClick(item.file)}
              >
                <div className="signon-summary-text">
                  <span className="team-name">
                    {item.team_name || 'Unknown team'}
                    {item.is_new && <span className="new-pill">New</span>}
                  </span>
                  <span className="child-name">{childName}</span>
                  {item.submitted_at && (
                    <span className="submitted-at">{formatSubmittedAt(item.submitted_at)}</span>
                  )}
                </div>
                <span className="chevron">
                  {isExpanded ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
                </span>
              </button>

              {isExpanded && (
                <div className="signon-detail">
                  {detailLoading === item.file && (
                    <div className="status-message">Loading details…</div>
                  )}
                  {detail && (
                    <>
                      <DetailSection
                        title="Overview"
                        data={detail}
                        excludeKeys={['parent', 'address', 'child', 'medical', 'declarations']}
                      />
                      <DetailSection title="Parent" data={detail.parent} />
                      <DetailSection title="Address" data={detail.address} />
                      <DetailSection title="Child" data={detail.child} />
                      <DetailSection title="Medical" data={detail.medical} />
                      <DetailSection title="Declarations" data={detail.declarations} />
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PlayerSignOnListPage;
